import { DAY_PARTING_DAYS, initialDaypartingDataObject } from "./constants";
import $ from "jquery";
import { populateCampaigns, sendRequest } from "./retrieve-data";
import {
    Campaign,
    IDaysArr,
    IDprCheckedData,
    IDprRange,
    TAllSlotsOnSubmit,
    TDaypartingData,
    TDprRule,
    TDprSlots,
} from "./common-types";
import { showToast } from "./modals";
import { setPillBadges } from "./utils";
import { setCampaignsData } from "@features/gridDataSlice";
import store from "../store";
import * as Sentry from "@sentry/react";
import {
    updateDaypartingDataObject,
    updateDayPartingDisplay,
    updateDaypartingEditCampaignData,
    updateIsDaypartingEdit,
} from "@features/modalSlice";
import React, { Dispatch, SetStateAction } from "react";

const createDaypartingRule = async (data: TDaypartingData) => {
    const response = await sendRequest({
        type: "dayparting/createRule",
        method: "POST",
        data: data,
    });
    return response;
};

const getDaypartingRules = async (campaigns: string[]) => {
    const response = await sendRequest({
        type: "dayparting/getRule",
        method: "POST",
        data: { campaigns },
    });
    return response;
};

export const editDaypartingRules = async (
    id: string[],
    dprTimeSlots: TDprSlots,
    setSelectedValue: React.Dispatch<SetStateAction<string>>
) => {
    const daypartingRules = (await getDaypartingRules(id)).message;
    setSelectedValue(daypartingRules[0].timezone);
    const updatedDaypartingData = { ...initialDaypartingDataObject };
    daypartingRules.forEach((data: TDprRule) => {
        const currentDay = DAY_PARTING_DAYS[data.day_of_week - 1].toLowerCase();
        const slotsArr = data.slots;
        const editDataArr: Array<number> = [];
        slotsArr.forEach((slot: IDprRange, index: number) => {
            dprTimeSlots[currentDay][`slot-${index + 1}`] = slot;
            dprTimeSlots[currentDay]["state"] = data.status;
            editDataArr.push(index + 1);
        });
        updatedDaypartingData[currentDay] = editDataArr;
    });
    store.dispatch(updateDaypartingEditCampaignData(id));
    store.dispatch(updateIsDaypartingEdit(true));
    store.dispatch(updateDaypartingDataObject(updatedDaypartingData));
    store.dispatch(updateDayPartingDisplay(true));
};

const addSlots = (
    day: IDaysArr,
    timeRanges: IDprRange[],
    currentDayIndex: number
) => {
    const allTimeSlots = timeRanges.map((timeRange: IDprRange) => {
        return {
            start: timeRange.start,
            end: timeRange.end,
            dayOfWeek: currentDayIndex,
        };
    });
    day.push(...allTimeSlots);
};

export const onSubmitClickHandler = async (
    selectedCampaignsData: string[],
    selectedValue: string,
    dprTimeSlots: TDprSlots,
    handleClose: () => void
) => {
    const {
        isDaypartingEdit,
        daypartingEditCampaignData,
        isAllCampaignsSelected,
        allSysCampaignId,
        clientTimezone
    } = store.getState();

    if (
        isAllCampaignsSelected.value
            ? allSysCampaignId.value.length
            : isDaypartingEdit.value
            ? daypartingEditCampaignData.value
            : selectedCampaignsData.length
    ) {
        const monday: IDaysArr = [];
        const tuesday: IDaysArr = [];
        const wednesday: IDaysArr = [];
        const thursday: IDaysArr = [];
        const friday: IDaysArr = [];
        const saturday: IDaysArr = [];
        const sunday: IDaysArr = [];
        const daySlots = [
            monday,
            tuesday,
            wednesday,
            thursday,
            friday,
            saturday,
            sunday,
        ];

        const dprState: Record<string, boolean> = {};

        DAY_PARTING_DAYS.forEach((day) => {
            const currentDay = day.toLowerCase();
            const currentDayIndex = DAY_PARTING_DAYS.indexOf(day) + 1;
            const slots: IDprRange[] = Object.values(
                dprTimeSlots[currentDay]
            ).filter((item) => item instanceof Object);
            const existingDprState = dprTimeSlots[currentDay].state;
            dprState[currentDay] = isDaypartingEdit.value
                ? existingDprState || false
                : true;
            switch (currentDay) {
                case "monday":
                    addSlots(monday, slots, currentDayIndex);
                    break;
                case "tuesday":
                    addSlots(tuesday, slots, currentDayIndex);
                    break;
                case "wednesday":
                    addSlots(wednesday, slots, currentDayIndex);
                    break;
                case "thursday":
                    addSlots(thursday, slots, currentDayIndex);
                    break;
                case "friday":
                    addSlots(friday, slots, currentDayIndex);
                    break;
                case "saturday":
                    addSlots(saturday, slots, currentDayIndex);
                    break;
                case "sunday":
                    addSlots(sunday, slots, currentDayIndex);
                    break;
            }
        });

        const allSlots = daySlots
            .filter(
                (slots) => slots.filter((slot) => slot.start && slot.end).length
            )
            .map((slots) => {
                return {
                    dayOfWeek: slots[0].dayOfWeek,
                    executionTime: slots.map((slot) => ({
                        start: slot.start,
                        end: slot.end,
                    })),
                    status: dprState[
                        DAY_PARTING_DAYS[slots[0].dayOfWeek - 1].toLowerCase()
                    ],
                };
            });

        const isAnySlotInvalid = allSlots.filter((slots: TAllSlotsOnSubmit) => {
            const isInvalidSlotPresent = slots.executionTime.filter(
                (data: IDprRange) =>
                    data.start === "" ||
                    data.start === null ||
                    data.end === "" ||
                    data.end === null
            );
            return isInvalidSlotPresent.length;
        });

        if (allSlots.length && !isAnySlotInvalid.length) {
            const data: TDaypartingData = {
                rules: {
                    action: "paused",
                    actionValue: "",
                    campaigns: isDaypartingEdit.value
                        ? daypartingEditCampaignData.value
                        : isAllCampaignsSelected.value
                        ? allSysCampaignId.value
                        : selectedCampaignsData,
                    slots: allSlots,
                },
                timezone: isDaypartingEdit.value
                    ? selectedValue
                    : clientTimezone.value,
            };

            try {
                const response = await createDaypartingRule(data);

                if (!response.status) {
                    throw new Error(response.message);
                }

                showToast("success", response.message);
                populateCampaigns()
                    .then((campaigns) => {
                        setPillBadges("campaigns-badge", (campaigns as Array<Campaign>).length);
                        store.dispatch(setCampaignsData(campaigns as Array<Campaign>));
                    })
                    .catch((error) => {
                        console.error(error);
                        Sentry.captureException(error);
                        showToast(
                            "danger",
                            "Unable to update the ad scheduling status. Please refresh the page"
                        );
                    });
                handleClose();
            } catch (e) {
                showToast(
                    "danger",
                    `Failed to apply ad scheduling : ${(e as Error).message}`
                );
            }
        } else {
            showToast("danger", "Invalid slots encountered");
        }
    } else {
        showToast("danger", `No campaign has been selected`);
    }
};

export const checkForOverlappingSlots = (
    cardId: string,
    timeSelected: string,
    dprTimeSlots: TDprSlots,
    setSelected: Dispatch<SetStateAction<string>>,
    setDprTimeSlots: Dispatch<SetStateAction<TDprSlots>>
) => {
    const day = cardId.split("-").shift();
    const cardKey = cardId.split("-").slice(-1)[0];
    const key = `slot-${cardId.split("-").pop()}`;
    const timeType = cardId.split("-").includes("end") ? "end" : "start";
    let startTime =
        timeType === "start"
            ? timeSelected
            : $(`#${day}-start-time-${cardKey}`).val();
    let endTime =
        timeType === "end"
            ? timeSelected
            : $(`#${day}-end-time-${cardKey}`).val();
    if (startTime) {
        $(`#${day}-end-time-${cardKey}`).prop("disabled", false);
    }
    // dprTimeSlots[day].hasOwnProperty(key)
    if (Object.prototype.hasOwnProperty.call(dprTimeSlots[day], key)) {
        dprTimeSlots[day][key][timeType] = timeSelected;
    } else {
        dprTimeSlots[day][key] = {
            start: startTime,
            end: endTime,
        };
    }

    if (timeType === "start") {
        if (endTime !== timeSelected) {
            $(`#${cardId}`).val(timeSelected);
            setSelected(timeSelected);
            $(`#${day}-time-card-${cardKey}`).css(
                "background-color",
                "rgb(0, 158, 255, 0.15)"
            );
            //
        } else if (endTime && timeSelected > endTime) {
            setSelected("");
            startTime = undefined;
            endTime = undefined;
            dprTimeSlots[day][key][timeType] = "";
            showToast("danger", `Start time should be before End time`);
            $(`#${day}-time-card-${cardKey}`).css(
                "background-color",
                "rgb(220, 53, 53,0.15)"
            );

            if ($(`#${day}-all-days-${cardKey}`).is(":checked")) {
                $(`#${day}-all-days-${cardKey}`).prop("disabled", false);
            } else {
                $(`#${day}-all-days-${cardKey}`).prop("disabled", true);
            }

            return;
        } else {
            setSelected("");
            endTime = undefined;
            dprTimeSlots[day][key][timeType] = "";

            $(`#${cardId}`).val("");

            showToast("danger", "Start time and end time cannot be same");
            $(`#${day}-time-card-${cardKey}`).css(
                "background-color",
                "rgb(220, 53, 53,0.15)"
            );

            $(`#${day}-all-days-${cardKey}`).prop("disabled", true);

            return;
        }
    } else {
        if (startTime < timeSelected) {
            setSelected(timeSelected);
            $(`#${day}-time-card-${cardKey}`).css(
                "background-color",
                "rgb(0, 158, 255, 0.15)"
            );

            $(`#${day}-all-days-${cardKey}`).prop("disabled", false);
        } else if (startTime && startTime > timeSelected) {
            setSelected("");
            startTime = undefined;
            endTime = undefined;
            dprTimeSlots[day][key][timeType] = "";
            showToast("danger", `Start time should be before End time`);
            $(`#${day}-time-card-${cardKey}`).css(
                "background-color",
                "rgb(220, 53, 53,0.15)"
            );

            $(`#${day}-all-days-${cardKey}`).prop("disabled", true);
            return;
        } else {
            setSelected("");
            startTime = undefined;
            dprTimeSlots[day][key][timeType] = "";

            $(`#${cardId}`).val("");
            showToast("danger", "Start time and end time cannot be same");
            $(`#${day}-time-card-${cardKey}`).css(
                "background-color",
                "rgb(220, 53, 53,0.15)"
            );

            $(`#${day}-all-days-${cardKey}`).prop("disabled", true);

            return;
        }
    }

    const slotsArray = Object.entries(dprTimeSlots[day]);

    type TSlots = {
        start: string;
        end: string;
    };

    for (let i = 0; i < slotsArray.length; i++) {
        const currentSlot: TSlots[] = slotsArray[i];
        const currentStart = currentSlot[1].start;
        const currentEnd = currentSlot[1].end;

        for (let j = 0; j < slotsArray.length; j++) {
            if (i === j) {
                continue;
            }
            const nextSlot: TSlots[] = slotsArray[j];
            const nextStart = nextSlot[1].start;
            const nextEnd = nextSlot[1].end;

            if ((currentStart && currentEnd) || (nextStart && nextEnd)) {
                if (
                    currentStart &&
                    currentEnd &&
                    nextStart &&
                    currentStart < nextStart &&
                    currentEnd > nextStart
                ) {
                    setSelected("");
                    startTime = undefined;
                    endTime = undefined;
                    dprTimeSlots[day][key][timeType] = "";
                    $(`#${day}-end-time-${cardKey}`).prop("disabled", true);
                    showToast("danger", `Slot overlapping with slot-${i + 1}`);
                    $(`#${day}-time-card-${cardKey}`).css(
                        "background-color",
                        "rgb(220, 53, 53,0.15)"
                    );

                    $(`#${day}-all-days-${cardKey}`).prop("disabled", true);

                    return;
                } else if (
                    nextStart &&
                    nextEnd &&
                    currentStart > nextStart &&
                    currentStart < nextEnd
                ) {
                    setSelected("");
                    startTime = undefined;
                    endTime = undefined;
                    dprTimeSlots[day][key][timeType] = "";
                    // $(`#${day}-end-time-${cardKey}`).prop('disabled', true);
                    showToast("danger", `Slot overlapping with slot-${i + 1}`);
                    $(`#${day}-time-card-${cardKey}`).css(
                        "background-color",
                        "rgb(220, 53, 53,0.15)"
                    );

                    $(`#${day}-all-days-${cardKey}`).prop("disabled", true);

                    return;
                } else if (
                    currentStart === nextStart ||
                    currentStart === nextEnd ||
                    currentEnd === nextStart ||
                    currentEnd === nextEnd
                ) {
                    setSelected("");
                    if (
                        currentStart === nextStart ||
                        currentStart === nextEnd
                    ) {
                        startTime = undefined;
                        dprTimeSlots[day][key][timeType] = "";
                    } else if (
                        currentEnd === nextStart ||
                        currentEnd === nextEnd
                    ) {
                        endTime = undefined;
                        dprTimeSlots[day][key][timeType] = "";
                    }
                    showToast("danger", `Slot overlapping with slot-${i + 1}`);
                    $(`#${day}-time-card-${cardKey}`).css(
                        "background-color",
                        "rgb(220, 53, 53,0.15)"
                    );

                    $(`#${day}-all-days-${cardKey}`).prop("disabled", true);
                    return;
                }
            }
        }
    }

    // toggleAddMoreSlotBtn(day,dprTimeSlots);
    const updatedDpr = { ...dprTimeSlots };
    setDprTimeSlots(updatedDpr);
};

type TNextSlot = Array<Record<string, string> | string | boolean>;
type TSlotsArr = Array<TNextSlot>;

export const timeSlotsValidation = (
    currentStart: string,
    currentEnd: string,
    dprTimeSlots: TDprSlots,
    currentDay: string,
    allDaysSlotConflicts: Array<TAllDaySlot>
) => {
    const slotsArray: TSlotsArr = Object.entries(dprTimeSlots[currentDay]);
    let isConflicting = false;
    for (let i = 0; i < slotsArray.length; i++) {
        const nextSlot: TNextSlot = slotsArray[i];
        const nextStart = (nextSlot[1] as Record<string, string>).start;
        const nextEnd = (nextSlot[1] as Record<string, string>).end;

        if (
            currentStart &&
            currentEnd &&
            nextStart &&
            currentStart < nextStart &&
            currentEnd > nextStart
        ) {
            allDaysSlotConflicts.push({
                slot: i + 1,
                day: currentDay,
            });
            isConflicting = true;
        } else if (
            nextStart &&
            nextEnd &&
            currentStart > nextStart &&
            currentStart < nextEnd
        ) {
            allDaysSlotConflicts.push({
                slot: i + 1,
                day: currentDay,
            });
            isConflicting = true;
        } else if (
            currentStart === nextStart ||
            currentStart === nextEnd ||
            currentEnd === nextStart ||
            currentEnd === nextEnd
        ) {
            if (currentStart === nextStart || currentStart === nextEnd) {
                isConflicting = true;
            } else if (currentEnd === nextStart || currentEnd === nextEnd) {
                isConflicting = true;
            }
            allDaysSlotConflicts.push({
                slot: i + 1,
                day: currentDay,
            });
        }
    }
    return isConflicting;
};

type TAddToAllDaysHandler = {
    day: string;
    containerKey: number;
    dprTimeSlots: TDprSlots;
    setDprTimeSlots: React.Dispatch<React.SetStateAction<TDprSlots>>;
    daypartingCardCheckedData: IDprCheckedData;
    setDaypartingCardCheckedData: React.Dispatch<
        React.SetStateAction<IDprCheckedData>
    >;
};

type TAllDaySlot = {
    slot: number;
    day: string;
};

export const addToAllDaysHandler = ({
    day,
    containerKey,
    dprTimeSlots,
    setDprTimeSlots,
    daypartingCardCheckedData,
    setDaypartingCardCheckedData,
}: TAddToAllDaysHandler) => {
    /**
     * ! Validations
     * * Add to all days => disable => end time in not selected
     * * If there is one card present without data then on selecting all days populate the data to that card
     * * While clicking all days btn add a check that the current array should not exceed length >10 for add more btn
     * * If any card is conflicting of already existing show a toast without adding a card
     * * Add timeslots validation for add to all
     *  */

    const daypartingCardCheckedDataObject = { ...daypartingCardCheckedData };
    const start = $(`#${day}-start-time-${containerKey}`).val() as string;
    const end = $(`#${day}-end-time-${containerKey}`).val() as string;
    const maxSlotsConflict: string[] = [];
    const daypartingDataObject = store.getState().daypartingDataObject.value;
    const updatedDaypartingData = {
        ...daypartingDataObject,
    };

    if ($(`#${day}-all-days-${containerKey}`).is(":checked")) {
        const allDaysSlotConflicts: TAllDaySlot[] = [];
        DAY_PARTING_DAYS.forEach((currentDay) => {
            const dayPartingDay = currentDay.toLowerCase();
            if (dayPartingDay !== day) {
                const isConflicting = timeSlotsValidation(
                    start,
                    end,
                    dprTimeSlots,
                    dayPartingDay,
                    allDaysSlotConflicts
                );
                if (!isConflicting) {
                    let slotKey = updatedDaypartingData[dayPartingDay].at(-1);

                    if (
                        Object.keys(dprTimeSlots[dayPartingDay]).filter(
                            (item) => item !== "state"
                        ).length < 10
                    ) {
                        if (updatedDaypartingData[dayPartingDay].length === 1) {
                            if (
                                !(
                                    Object.keys(
                                        dprTimeSlots[dayPartingDay]
                                    ).filter((item) => item !== "state")
                                        .length === 0
                                )
                            ) {
                                updatedDaypartingData[dayPartingDay] = [
                                    ...updatedDaypartingData[dayPartingDay],
                                    updatedDaypartingData[dayPartingDay].at(
                                        -1
                                    ) + 1,
                                ];

                                slotKey =
                                    updatedDaypartingData[dayPartingDay].at(-1);
                            }
                        } else {
                            if (
                                updatedDaypartingData[dayPartingDay].length ===
                                Object.keys(dprTimeSlots[dayPartingDay]).filter(
                                    (item) => item !== "state"
                                ).length
                            ) {
                                updatedDaypartingData[dayPartingDay] = [
                                    ...updatedDaypartingData[dayPartingDay],
                                    updatedDaypartingData[dayPartingDay].at(
                                        -1
                                    ) + 1,
                                ];
                                slotKey =
                                    updatedDaypartingData[dayPartingDay].at(-1);
                            }
                        }

                        dprTimeSlots[dayPartingDay][`slot-${slotKey}`] = {
                            start: start,
                            end: end,
                        };

                        /**
                         * ! For updating add-to-all-days btn status
                         */

                        daypartingCardCheckedDataObject[dayPartingDay][
                            `slot-${containerKey}`
                        ] = false;
                    } else {
                        maxSlotsConflict.push(dayPartingDay);

                        if (
                            maxSlotsConflict.length &&
                            dayPartingDay === "sunday"
                        ) {
                            let errorMessage = "";
                            maxSlotsConflict.forEach((conflictedDay) => {
                                errorMessage += `${conflictedDay}, `;
                            });
                            errorMessage = errorMessage.slice(0, -2);
                            showToast(
                                "danger",
                                `Maximum slots reached for ${errorMessage}`
                            );
                        }
                    }
                } else {
                    let errorMessage = "";
                    allDaysSlotConflicts.forEach(
                        (conflictSlot: { slot: number; day: string }) => {
                            errorMessage += `${conflictSlot.day}, `;
                        }
                    );

                    errorMessage = errorMessage.slice(0, -2);
                    showToast(
                        "danger",
                        `Slot conflicts occured in ${errorMessage}`
                    );
                }
            } else {
                /**
                 * ! For updating add-to-all-days btn status
                 */

                daypartingCardCheckedDataObject[dayPartingDay][
                    `slot-${containerKey}`
                ] = true;
            }
        });
        store.dispatch(updateDaypartingDataObject(updatedDaypartingData));
        const updateDaypartingCardCheckedData = {
            ...daypartingCardCheckedDataObject,
        };
        setDaypartingCardCheckedData(updateDaypartingCardCheckedData);
    } else {
        const dispatchDayPartingData: boolean[] = [];
        DAY_PARTING_DAYS.forEach((currentDay) => {
            const dayPartingDay = currentDay.toLowerCase();
            const currentAvailableSlots = Object.keys(
                dprTimeSlots[dayPartingDay]
            ).filter((item) => item !== "state");
            if (dayPartingDay !== day) {
                if (currentAvailableSlots.length !== 1) {
                    currentAvailableSlots.forEach((slot) => {
                        const slotNumber = slot.split("-").pop();
                        if (
                            dprTimeSlots[dayPartingDay][`slot-${slotNumber}`][
                                "start"
                            ] === start &&
                            dprTimeSlots[dayPartingDay][`slot-${slotNumber}`][
                                "end"
                            ] === end
                        ) {
                            dispatchDayPartingData.push(true);
                            delete dprTimeSlots[dayPartingDay][
                                `slot-${slotNumber}`
                            ];
                            delete daypartingCardCheckedDataObject[
                                dayPartingDay
                            ][`slot-${slotNumber}`];
                            updatedDaypartingData[dayPartingDay] =
                                updatedDaypartingData[dayPartingDay].filter(
                                    (daySlot: number) =>
                                        daySlot.toString() !== slotNumber
                                );
                        }
                    });
                } else {
                    const slotNumber = currentAvailableSlots[0]
                        .split("-")
                        .pop();
                    if (
                        dprTimeSlots[dayPartingDay][`slot-${slotNumber}`][
                            "start"
                        ] === start &&
                        dprTimeSlots[dayPartingDay][`slot-${slotNumber}`][
                            "end"
                        ] === end
                    ) {
                        dispatchDayPartingData.push(false);
                        delete dprTimeSlots[dayPartingDay][
                            `slot-${slotNumber}`
                        ];
                        delete daypartingCardCheckedDataObject[dayPartingDay][
                            `slot-${slotNumber}`
                        ];
                        (
                            document.getElementById(
                                `${dayPartingDay}-start-time-${slotNumber}`
                            ) as HTMLSelectElement
                        ).value = "";
                        (
                            document.getElementById(
                                `${dayPartingDay}-end-time-${slotNumber}`
                            ) as HTMLSelectElement
                        ).value = "";
                    }
                }
            } else {
                daypartingCardCheckedDataObject[day][`slot-${containerKey}`] =
                    false;
            }
        });

        if (dispatchDayPartingData.includes(true)) {
            store.dispatch(updateDaypartingDataObject(updatedDaypartingData));
        }
    }

    const updatedDpr = { ...dprTimeSlots };
    setDprTimeSlots(updatedDpr);
    const updateDaypartingCardCheckedData = {
        ...daypartingCardCheckedDataObject,
    };
    setDaypartingCardCheckedData(updateDaypartingCardCheckedData);
};

export const dprToggleChangeHandler = (
    dprTimeSlots: TDprSlots,
    setDprTimeSlots: React.Dispatch<SetStateAction<TDprSlots>>,
    day = "all"
) => {
    if (day === "all") {
        const currentState = $("#toggle-all-dpr").is(":checked");
        DAY_PARTING_DAYS.forEach((dprDay) => {
            const currentDay = dprDay.toLowerCase();
            $(`#toggle-${currentDay}-dpr`).prop("checked", currentState);
            dprTimeSlots[currentDay]["state"] = currentState;
        });
    } else {
        const currentState = $(`#toggle-${day}-dpr`).is(":checked");
        dprTimeSlots[day]["state"] = currentState;
    }
    const updatedDprData = { ...dprTimeSlots };
    setDprTimeSlots(updatedDprData);
};

export const toggleAddMoreSlotBtn = (
    currentCardDay: string,
    dprTimeSlots: TDprSlots
) => {
    const daypartingDataObject = store.getState().daypartingDataObject.value;
    const currentCardDayCount = daypartingDataObject[currentCardDay].length;

    const isAllSlotValid: boolean =
        Object.values(dprTimeSlots[currentCardDay]).filter(
            (item: IDprRange) =>
                item instanceof Object && item.start && item.end
        ).length === currentCardDayCount;
    if (!isAllSlotValid) {
        $(`#add-time-card-btn-${currentCardDay}`).css({
            "pointer-events": "none",
        });
        $(`#add-time-card-btn-label-${currentCardDay}`).css({
            color: "rgba(0, 158, 255, 0.6)",
        });
    } else {
        $(`#add-time-card-btn-${currentCardDay}`).css({
            "pointer-events": "auto",
        });
        $(`#add-time-card-btn-label-${currentCardDay}`).css({
            color: "rgba(0, 158, 255)",
        });
    }
};

export const disableDayParting = async () => {

    try {

        const {
            campaignsSelectionData,
        } = store.getState();

        const campaignIds = campaignsSelectionData.value ?
            campaignsSelectionData.value.map((campaign) => campaign.sys_campaign_id) : []

        const response = await sendRequest({
            type: `dayparting/disable?campaignIds=${campaignIds.length ? campaignIds : []}`,
            method: "PATCH",
            data: { campaigns: campaignIds },
        });


            showToast("success", response.message);
            populateCampaigns()
                .then((campaigns) => {
                    setPillBadges("campaigns-badge", (campaigns as Array<Campaign>).length);
                    store.dispatch(setCampaignsData(campaigns as Array<Campaign>));
                })
                .catch((error) => {
                    console.error(error);
                    Sentry.captureException(error);
                    showToast(
                        "danger",
                        "Unable to update the day-parting status. Please refresh the page"
                    );
                });

    } catch (err) {
        showToast(
            "danger",
            `Failed to disable day-parting rules : ${(err as Error).message}`
        );
    }

}

export const deleteDayParting = async () => {
    try {
        const {
            campaignsSelectionData,
        } = store.getState();

        const campaignIds = campaignsSelectionData.value ?
            campaignsSelectionData.value.map((campaign) => campaign.sys_campaign_id) : []

        const response = await sendRequest({
            type: `dayparting/delete?campaignIds=${campaignIds.length ? campaignIds : []}`,
            method: "DELETE",
            data: { campaigns: campaignIds },
        });

            showToast("success", response.message);
            populateCampaigns()
                .then((campaigns) => {
                    setPillBadges("campaigns-badge", (campaigns as Array<Campaign>).length);
                    store.dispatch(setCampaignsData(campaigns as Array<Campaign>));
                })
                .catch((error) => {
                    console.error(error);
                    Sentry.captureException(error);
                    showToast(
                        "danger",
                        "Unable to update the day-parting status. Please refresh the page"
                    );
                });
    } catch (err) {
        showToast(
            "danger",
            `Failed to delete day-parting rules : ${(err as Error).message}`
        );
    }

}